import type { GeoJSONFeature, Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';
import { type RefObject, useCallback, useRef } from 'react';
import { registerMapEvent } from '@/libs/utils/registerMapEvents';
import { dealFeatureAt } from '../utils/dealHitTest';
import { MapLayersService } from '../utils/MapLayersService';

/**
 * Click-to-inspect for the deals layer: a click reports the deal's id through
 * `onDealSelect`; the clicked mark's ring stays unfolded while the card is open.
 */
export function useDealsInteractions(
  map: RefObject<MapboxMap | null>,
  onDealSelect: (dealId: string | null) => void,
) {
  // 'preclick' runs before 'click', so telling a re-click from a new one needs
  // the key snapshotted there.
  const openKeyRef = useRef<string | null>(null);
  const preclickKeyRef = useRef<string | null>(null);
  const hoveredKeyRef = useRef<string | null>(null);

  // The listeners bind once (setupDealsInteractions runs in a mount effect), so
  // they must read the latest callback through a ref: a captured onDealSelect
  // carries the router-state snapshot of the render it was bound on, and
  // navigating from it rewinds any param changed since (see
  // usePersistedSearchParam's setter note).
  const onDealSelectRef = useRef(onDealSelect);
  onDealSelectRef.current = onDealSelect;

  function setExpanded(key: string, expanded: boolean) {
    const currentMap = map.current;
    if (!currentMap || !key) return;
    new MapLayersService(currentMap).setDealExpanded(key, expanded);
  }

  function dealAt(event: MapMouseEvent): GeoJSONFeature | undefined {
    const currentMap = map.current;
    if (!currentMap) return undefined;
    return dealFeatureAt(currentMap, event.point);
  }

  function handleMouseMove(event: MapMouseEvent) {
    const feature = dealAt(event);
    const key = feature ? String((feature.properties as { id?: string }).id ?? '') : null;
    if (key === hoveredKeyRef.current) return;

    if (hoveredKeyRef.current && hoveredKeyRef.current !== openKeyRef.current) {
      setExpanded(hoveredKeyRef.current, false);
    }
    hoveredKeyRef.current = key;
    if (key && key !== openKeyRef.current) setExpanded(key, true);
  }

  // Without this, a mark hovered when the cursor leaves the canvas stays unfolded.
  function handleMouseOut() {
    if (hoveredKeyRef.current && hoveredKeyRef.current !== openKeyRef.current) {
      setExpanded(hoveredKeyRef.current, false);
    }
    hoveredKeyRef.current = null;
  }

  function handlePreclick() {
    preclickKeyRef.current = openKeyRef.current;
    // No deal open: nothing to close, and calling the setter anyway would fire
    // a router navigation (and drop the persisted deal) on every idle map click.
    if (openKeyRef.current === null) return;
    if (openKeyRef.current !== hoveredKeyRef.current) {
      setExpanded(openKeyRef.current, false);
    }
    openKeyRef.current = null;
    onDealSelectRef.current(null);
  }

  function handleClick(event: MapMouseEvent) {
    const feature = dealAt(event);
    if (!feature) return;

    const key = String((feature.properties as { id?: string }).id ?? '');
    if (!key) return;
    const wasOpenKey = preclickKeyRef.current;
    preclickKeyRef.current = null;
    // Re-clicking the open deal closes it, which preclick already did.
    if (wasOpenKey === key) return;

    openKeyRef.current = key;
    setExpanded(key, true);
    onDealSelectRef.current(key);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    if (openKeyRef.current === null) return;
    if (openKeyRef.current !== hoveredKeyRef.current) {
      setExpanded(openKeyRef.current, false);
    }
    openKeyRef.current = null;
    onDealSelectRef.current(null);
  }

  // The card's close affordances land here so the open ring folds back with the card.
  const clearSelectedDeal = useCallback(() => {
    const currentMap = map.current;
    if (currentMap && openKeyRef.current && openKeyRef.current !== hoveredKeyRef.current) {
      new MapLayersService(currentMap).setDealExpanded(openKeyRef.current, false);
    }
    openKeyRef.current = null;
    onDealSelectRef.current(null);
  }, [map]);

  function setupDealsInteractions() {
    const currentMap = map.current;
    if (!currentMap) return () => {};

    // No chip listener: `useClusterInteractions` owns that click for both kinds.
    const cleanupPreclick = registerMapEvent(currentMap, 'preclick', handlePreclick);
    const cleanupClick = registerMapEvent(currentMap, 'click', handleClick);
    const cleanupMouseMove = registerMapEvent(currentMap, 'mousemove', handleMouseMove);
    const cleanupMouseOut = registerMapEvent(currentMap, 'mouseout', handleMouseOut);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (hoveredKeyRef.current) setExpanded(hoveredKeyRef.current, false);
      if (openKeyRef.current && openKeyRef.current !== hoveredKeyRef.current) {
        setExpanded(openKeyRef.current, false);
      }
      hoveredKeyRef.current = null;
      openKeyRef.current = null;
      cleanupMouseOut();
      cleanupMouseMove();
      cleanupClick();
      cleanupPreclick();
    };
  }

  return { setupDealsInteractions, clearSelectedDeal };
}
